import { trackContactClick, trackCtaClick, trackNavClick } from "#utils/utils";
import { EmailIcon, PhoneIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Container,
  Divider,
  HStack,
  Heading,
  Icon,
  Image,
  Link,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import * as React from "react";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTwitter,
} from "react-icons/fa/index.js";
import { SocialButton } from "./SocialButton";

interface FooterLinkProps {
  href: string;
  label: string;
  navType?: string;
}

const FooterLink = ({ href, label, navType = "footer" }: FooterLinkProps) => {
  const hoverColor = useColorModeValue("white", "gray.900");
  return (
    <Link
      href={href}
      fontSize="sm"
      opacity={0.75}
      _hover={{ opacity: 1, color: hoverColor, textDecoration: "none" }}
      transition="all 0.15s"
      onClick={() =>
        trackNavClick({ nav_type: navType, link_text: label, link_url: href })
      }
    >
      {label}
    </Link>
  );
};

const Footer = () => {
  const bgColor = useColorModeValue("gray.900", "gray.50");
  const textColor = useColorModeValue("gray.300", "gray.600");
  const borderColor = useColorModeValue("gray.700", "gray.300");
  const headingColor = useColorModeValue("white", "gray.800");
  const taglineColor = useColorModeValue("gray.400", "gray.500");

  return (
    <Box bg={bgColor} color={textColor}>
      <Container maxW="8xl" py={{ base: 10, md: 16 }}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={{ base: 8, lg: 6 }}>

          {/* Col 1: Brand */}
          <Stack spacing={5} gridColumn={{ lg: "span 1" }}>
            <Image
              height={10}
              width="auto"
              src="/img/logo-no-background.webp"
              alt="TenK Solutions"
            />
            <Text fontSize="sm" color={taglineColor} lineHeight="1.6">
              Business Operations Automation for growing organizations.
              DMV-based. Nationally delivered.
            </Text>
            <VStack spacing={2} align="flex-start">
              <Link
                href="tel:+12025737837"
                fontSize="sm"
                opacity={0.75}
                _hover={{ opacity: 1 }}
                onClick={() =>
                  trackContactClick({ method: "phone", contact_value: "+12025737837" })
                }
              >
                <HStack spacing={2}>
                  <Icon as={PhoneIcon} boxSize={3.5} />
                  <Text>202.573.7837</Text>
                </HStack>
              </Link>
              <Link
                href="mailto:hello@tenksolutions.com"
                fontSize="sm"
                opacity={0.75}
                _hover={{ opacity: 1 }}
                onClick={() =>
                  trackContactClick({ method: "email", contact_value: "hello@tenksolutions.com" })
                }
              >
                <HStack spacing={2}>
                  <Icon as={EmailIcon} boxSize={3.5} />
                  <Text>hello@tenksolutions.com</Text>
                </HStack>
              </Link>
            </VStack>
            <HStack spacing={2}>
              <SocialButton label="LinkedIn" bg="none" color="gray.400" variant="outline" href="https://www.linkedin.com/company/tenksolutions">
                <FaLinkedin />
              </SocialButton>
              <SocialButton label="Twitter" bg="none" color="gray.400" variant="outline" href="https://twitter.com/tenk_solutions">
                <FaTwitter />
              </SocialButton>
              <SocialButton label="Facebook" bg="none" color="gray.400" variant="outline" href="https://www.facebook.com/tenksolutionsllc">
                <FaFacebook />
              </SocialButton>
              <SocialButton label="Instagram" bg="none" color="gray.400" variant="outline" href="https://www.instagram.com/tenksolutions">
                <FaInstagram />
              </SocialButton>
            </HStack>
          </Stack>

          {/* Col 2: Solutions */}
          <Stack spacing={4}>
            <Heading size="sm" color={headingColor} textTransform="uppercase" letterSpacing="wider">
              Solutions
            </Heading>
            <Stack spacing={2}>
              <FooterLink href="/solutions" label="All Solutions" />
              <FooterLink href="/solutions/client-intake" label="Client Intake Automation" />
              <FooterLink href="/solutions/knowledge-assistant" label="Knowledge Assistant" />
              <FooterLink href="/solutions/executive-dashboard" label="Executive Dashboard" />
              <FooterLink href="/calculator" label="ROI Calculator" />
            </Stack>
          </Stack>

          {/* Col 3: Company */}
          <Stack spacing={4}>
            <Heading size="sm" color={headingColor} textTransform="uppercase" letterSpacing="wider">
              Company
            </Heading>
            <Stack spacing={2}>
              <FooterLink href="/about-us" label="About Us" />
              <FooterLink href="/case-studies" label="Case Studies" />
              <FooterLink href="/partners" label="Partners" />
              <FooterLink href="/articles" label="Blog & Resources" />
              <FooterLink href="/contact-us" label="Contact Us" />
            </Stack>
          </Stack>

          {/* Col 4: Resources */}
          <Stack spacing={4}>
            <Heading size="sm" color={headingColor} textTransform="uppercase" letterSpacing="wider">
              Resources
            </Heading>
            <Stack spacing={2}>
              <FooterLink href="/pricing" label="Pricing" />
              <FooterLink href="/how-it-works" label="How It Works" />
              <FooterLink href="/assessments" label="Free Assessments" />
              <FooterLink href="/calculator" label="ROI Calculator" />
              <FooterLink href="/privacy" label="Privacy Policy" />
            </Stack>
          </Stack>

          {/* Col 5: CTA */}
          <Stack spacing={4}>
            <Heading size="sm" color={headingColor} textTransform="uppercase" letterSpacing="wider">
              Get Started
            </Heading>
            <Text fontSize="sm" color={taglineColor} lineHeight="1.6">
              Ready to eliminate your biggest operational bottleneck?
            </Text>
            <Link href="/contact-us?interest=discovery" _hover={{ textDecoration: "none" }}>
              <Button
                onClick={() =>
                  trackCtaClick({
                    cta_label: "Book Discovery Call",
                    cta_location: "Footer",
                    destination_url: "/contact-us?interest=discovery",
                  })
                }
                colorScheme="blue"
                size="md"
                width="full"
                _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                transition="all 0.2s"
              >
                Book a Discovery Call
              </Button>
            </Link>
            <Link href="/assessments" _hover={{ textDecoration: "none" }}>
              <Button
                onClick={() =>
                  trackCtaClick({
                    cta_label: "Free Assessment",
                    cta_location: "Footer",
                    destination_url: "/assessments",
                  })
                }
                variant="outline"
                colorScheme="whiteAlpha"
                color={textColor}
                borderColor={borderColor}
                size="md"
                width="full"
                _hover={{ borderColor: "gray.400", transform: "translateY(-2px)" }}
                transition="all 0.2s"
              >
                Free Assessment
              </Button>
            </Link>
          </Stack>
        </SimpleGrid>

        <Divider borderColor={borderColor} mt={12} mb={6} />

        {/* Bottom Bar */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} alignItems="center">
          <Text fontSize="xs" color={taglineColor}>
            © {new Date().getFullYear()} TenK Solutions, LLC. All rights reserved.
          </Text>
          <Text fontSize="xs" color={taglineColor} textAlign={{ base: "left", md: "center" }}>
            Business Operations Automation · Washington, DC Metropolitan Area
          </Text>
          <HStack spacing={4} justify={{ base: "flex-start", md: "flex-end" }}>
            <SocialButton label="LinkedIn" href="https://www.linkedin.com/company/tenksolutions">
              <FaLinkedin />
            </SocialButton>
            <SocialButton label="Twitter" href="https://twitter.com/tenk_solutions">
              <FaTwitter />
            </SocialButton>
            <SocialButton label="Facebook" href="https://www.facebook.com/tenksolutionsllc">
              <FaFacebook />
            </SocialButton>
            <SocialButton label="Instagram" href="https://www.instagram.com/tenksolutions">
              <FaInstagram />
            </SocialButton>
          </HStack>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default Footer;
